/*
 * Portions of this code are from Novel (https://github.com/steven-tey/novel)
 * Copyright (c) 2023 Steven Tey
 * Licensed under the Apache License, Version 2.0
 */

import { getLinkMetadataApi } from '@/api-calls/link-metadata/get'
import { cn } from '@/utils/cn'
import { Node, mergeAttributes } from '@tiptap/core'
import type { Mark, Node as ProseMirrorNode } from '@tiptap/pm/model'
import {
  NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react'
import { ExternalLink } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { toast } from 'sonner'

interface LinkCardAttributes {
  url: string
  title: string | null
  description: string | null
  image: string | null
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    linkCard: {
      /**
       * Insert a link card
       * @param attributes The link card attributes
       * @example editor.commands.insertLinkCard({ url: 'https://example.com', title: 'Example', description: 'Description', image: 'https://example.com/image.jpg' })
       */
      insertLinkCard: (attributes: LinkCardAttributes) => ReturnType
      /**
       * Convert a link mark to a link card
       */
      convertLinkToCard: () => ReturnType
    }
  }
}

// Shared class constants for link card styling
// These are used in both the React component and renderHTML to ensure consistency
const LINK_CARD_CLASSES = {
  wrapper: 'not-prose my-4 node-link-card',
  cardContainer:
    'group relative flex flex-row items-stretch overflow-hidden rounded-lg border border-border bg-card transition-all hover:shadow-md cursor-pointer',
  contentArea: 'flex flex-col gap-2 p-3 md:p-4 flex-1 min-w-0',
  title: 'line-clamp-1 text-sm md:text-base font-semibold leading-tight m-0',
  description: 'line-clamp-2 text-xs md:text-sm text-muted-foreground m-0',
  hostname: 'flex items-center gap-1.5 text-xs text-muted-foreground mt-auto',
  imageContainer:
    'relative w-1/3 max-h-48 flex-shrink-0 overflow-hidden bg-muted flex items-center',
  image: 'w-full h-full object-cover block',
  link: 'block no-underline text-inherit',
} as const

// React Component for the Link Card Node View
const LinkCardComponent: React.FC<NodeViewProps> = ({
  node,
  updateAttributes,
  selected,
  deleteNode,
}) => {
  const { url, title, description, image } = node.attrs as LinkCardAttributes
  const [isLoading, setIsLoading] = useState(!title && !description && !image)

  // Fetch metadata when component mounts if not already loaded
  useEffect(() => {
    if (!url || (title && description)) return

    // Check if it's a Twitter or YouTube URL (should be handled by their extensions)
    const isTwitterUrl = /(https?:\/\/)?(www\.)?x\.com\//.test(url)
    const isYoutubeUrl =
      /(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\//.test(url)

    if (isTwitterUrl || isYoutubeUrl) {
      return
    }

    setIsLoading(true)
    const loadingToast = toast.loading('Fetching link metadata...')

    getLinkMetadataApi
      .queryFn({ url })
      .then((metadata) => {
        if (metadata) {
          updateAttributes({
            url: metadata.url,
            title: metadata.title,
            description: metadata.description,
            image: metadata.image,
          })
          toast.dismiss(loadingToast)
          toast.success('Link metadata loaded')
        }
      })
      .catch((error) => {
        console.error('Error fetching link metadata:', error)
        toast.dismiss(loadingToast)
        toast.error('Failed to fetch link metadata')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [url, title, description, image, updateAttributes])

  const handleClick = () => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <NodeViewWrapper
      className={cn(LINK_CARD_CLASSES.wrapper, 'ignore-prosemirror', {
        'ring-2 ring-primary rounded-lg': selected,
      })}
      data-node-view-wrapper
      contentEditable={false}
    >
      <div
        className={LINK_CARD_CLASSES.cardContainer}
        onClick={handleClick}
        contentEditable={false}
      >
        {isLoading ? (
          <div className="flex items-center justify-center p-8 w-full">
            <div className="text-sm text-muted-foreground">
              Loading preview...
            </div>
          </div>
        ) : (
          <>
            <div className={LINK_CARD_CLASSES.contentArea}>
              {title && <div className={LINK_CARD_CLASSES.title}>{title}</div>}
              {description && (
                <div className={LINK_CARD_CLASSES.description}>
                  {description}
                </div>
              )}
              <div className={LINK_CARD_CLASSES.hostname}>
                <ExternalLink className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{new URL(url).hostname}</span>
              </div>
            </div>
            {image && (
              <div className={LINK_CARD_CLASSES.imageContainer}>
                <img
                  src={image}
                  alt={title || 'Link preview'}
                  className={LINK_CARD_CLASSES.image}
                  style={{ minHeight: 0 }}
                  onError={(e) => {
                    // Hide image on error
                    const target = e.target as HTMLImageElement
                    target.style.display = 'none'
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
    </NodeViewWrapper>
  )
}

// TipTap Extension Definition
export const LinkCardExtension = Node.create({
  name: 'linkCard',

  group: 'block',

  atom: true,

  addAttributes() {
    return {
      url: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-url') || '',
        renderHTML: (attributes) => {
          if (!attributes.url) return {}
          return { 'data-url': attributes.url }
        },
      },
      title: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-title'),
        renderHTML: (attributes) => {
          if (!attributes.title) return {}
          return { 'data-title': attributes.title }
        },
      },
      description: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-description'),
        renderHTML: (attributes) => {
          if (!attributes.description) return {}
          return { 'data-description': attributes.description }
        },
      },
      image: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-image'),
        renderHTML: (attributes) => {
          if (!attributes.image) return {}
          return { 'data-image': attributes.image }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="link-card"]',
        getAttrs: (element: HTMLElement | string) => {
          const domElement = element as HTMLElement
          return {
            url: domElement.getAttribute('data-url') || '',
            title: domElement.getAttribute('data-title'),
            description: domElement.getAttribute('data-description'),
            image: domElement.getAttribute('data-image'),
          }
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes, node }) {
    const { url, title, description, image } = node.attrs

    if (!url) {
      return [
        'div',
        mergeAttributes(HTMLAttributes, { 'data-type': 'link-card' }),
      ]
    }

    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'link-card',
        'data-component': 'link-card',
        'data-url': url || '',
        ...(title && { 'data-title': title }),
        ...(description && { 'data-description': description }),
        ...(image && { 'data-image': image }),
      }),
      [
        'a',
        {
          href: url,
          target: '_blank',
          rel: 'noopener noreferrer',
          class: LINK_CARD_CLASSES.link,
        },
        [
          'div',
          {
            class: LINK_CARD_CLASSES.cardContainer,
          },
          [
            'div',
            { class: LINK_CARD_CLASSES.contentArea },
            title
              ? [
                  'div',
                  {
                    class: LINK_CARD_CLASSES.title,
                  },
                  title,
                ]
              : null,
            description
              ? [
                  'div',
                  {
                    class: LINK_CARD_CLASSES.description,
                  },
                  description,
                ]
              : null,
            [
              'div',
              {
                class: LINK_CARD_CLASSES.hostname,
              },
              ['span', { class: 'truncate' }, url ? new URL(url).hostname : ''],
            ],
          ],
          image
            ? [
                'div',
                {
                  class: LINK_CARD_CLASSES.imageContainer,
                },
                [
                  'img',
                  {
                    src: image,
                    alt: title || 'Link preview',
                    class: LINK_CARD_CLASSES.image,
                  },
                ],
              ]
            : null,
        ],
      ],
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(LinkCardComponent)
  },

  addCommands() {
    return {
      insertLinkCard:
        (attributes: LinkCardAttributes) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: attributes,
          })
        },
      /**
       * Convert a link mark to a link card
       */
      convertLinkToCard:
        () =>
        ({ editor, commands }) => {
          if (!editor) return false

          const { state } = editor
          const { selection } = state
          const { from, to } = selection

          // Get the link mark from the selection
          const linkMark = state.schema.marks.link
          if (!linkMark) return false

          // Find link marks in the selection
          let linkHref: string | null = null

          state.doc.nodesBetween(from, to, (node: ProseMirrorNode) => {
            if (node.marks) {
              const linkMarkInstance = node.marks.find(
                (mark: Mark) => mark.type === linkMark,
              )
              if (linkMarkInstance?.attrs.href) {
                linkHref = linkMarkInstance.attrs.href
                return false // Stop searching
              }
            }
          })

          // Also check if the selection itself has a link mark
          const marks = state.storedMarks || selection.$from.marks()
          const linkMarkInstance = marks.find(
            (mark: Mark) => mark.type === linkMark,
          )
          if (linkMarkInstance?.attrs.href) {
            linkHref = linkMarkInstance.attrs.href
          }

          if (!linkHref) return false

          // Check if it's a Twitter or YouTube URL (let those extensions handle it)
          const isTwitterUrl = /(https?:\/\/)?(www\.)?x\.com\//.test(linkHref)
          const isYoutubeUrl =
            /(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\//.test(linkHref)

          if (isTwitterUrl || isYoutubeUrl) {
            return false
          }

          // Replace the link with a link card using commands API
          return commands.insertContentAt(
            { from, to },
            {
              type: this.name,
              attrs: {
                url: linkHref,
                title: null,
                description: null,
                image: null,
              },
            },
          )
        },
    }
  },
})
